import React, { useState } from 'react';
import { Toast, ValidatingInput, ValidationForm, ValidationSummary } from '@ygtechnologies/bygr-frontend-component-library';
import { MacGyverAccessLevel } from '@/services/auth';
import { MacGyverUser, updateMacGyverUser } from '@/services/macgyverUsers';
import { Checkbox, Button, ButtonBar, Dialog, Select } from '@ygtechnologies/rubus-ui';
import { extractErrorMessage } from '@/utils/ajax';
import { accessLevelOptions } from './shared';

interface MacGyverUserModalProps {
    isOpen: boolean;
    toggle(): void;
    onSave(): void;
    /** The user being edited. Creating users goes through MacGyverUsersBulkAddModal instead. */
    user: MacGyverUser;
}

const MacGyverUserModal: React.FC<MacGyverUserModalProps> = ({ isOpen, toggle, onSave, user }) => {
    const [email, setEmail] = useState(user.email);
    const [accessLevel, setAccessLevel] = useState<MacGyverAccessLevel>(user.accessLevel);
    const [isActive, setIsActive] = useState(user.isActive);
    const [isSaving, setIsSaving] = useState(false);

    // Re-seed from the row each time the modal opens (one instance serves every row).
    const handleOpen = () => {
        setEmail(user.email);
        setAccessLevel(user.accessLevel);
        setIsActive(user.isActive);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await updateMacGyverUser(user.id, { email, accessLevel, isActive });
            Toast.success('User updated');
            onSave();
            toggle();
        } catch (e) {
            const msg = await extractErrorMessage(e);
            Toast.error(msg ?? 'Something went wrong');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && toggle()} onOpen={handleOpen}>
            <Dialog.Content contentWidth="sm">
                <Dialog.Header>Edit user</Dialog.Header>
                <ValidationForm onSubmit={handleSave}>
                    <div className="form columns-1">
                        <label>
                            Email
                            <ValidatingInput
                                value={email}
                                required
                                fieldName="Email"
                                placeholder="user@example.com"
                                className="form-control"
                                // Discourage password-manager autofill on this field.
                                autoComplete="off"
                                data-1p-ignore
                                data-lpignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </label>
                        <label>
                            Access level
                            <Select
                                block
                                inputSize="sm"
                                options={accessLevelOptions}
                                value={accessLevel}
                                onChange={setAccessLevel}
                            />
                        </label>
                        <Checkbox inputSize="md" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}>
                            Is Active
                        </Checkbox>
                    </div>
                    <ValidationSummary />
                    <ButtonBar>
                        <ButtonBar.Right>
                            <Button onClick={toggle} variant="tertiary">
                                Cancel
                            </Button>
                            <Button loading={isSaving} type="submit" variant="primary">
                                Save
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ValidationForm>
            </Dialog.Content>
        </Dialog>
    );
};

export default MacGyverUserModal;
